import { t as uiText } from '../i18n';
import React, { useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import {
  alertsFromUsageDoc,
  alertsFromWelcomeBonus,
  welcomeBonusView,
  alertSignature,
  creditAlertDismissKey,
  creditPeriodId,
  type CreditAlertItem,
} from '../features/credits/workspaceCreditAlert';

type CreditUsageAlertProps = {
  workspaceId: string;
  enabled: boolean;
};

function readDismissed(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeDismissed(key: string) {
  try {
    window.localStorage.setItem(key, '1');
  } catch {
    // ignore
  }
}

export default function CreditUsageAlert({ workspaceId, enabled }: CreditUsageAlertProps) {
  const [items, setItems] = useState<CreditAlertItem[]>([]);
  const [period, setPeriod] = useState(creditPeriodId());
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (!enabled || !workspaceId) {
      setItems([]);
      setModalOpen(false);
      return;
    }
    let cancelled = false;
    const periodId = creditPeriodId();
    setPeriod(periodId);
    const run = async () => {
      try {
        const [snap, grantSnap] = await Promise.all([
          getDoc(doc(db, 'workspaces', workspaceId, 'creditUsage', periodId)),
          getDoc(doc(db, 'workspaces', workspaceId, 'creditGrants', 'welcomeBonus')).catch(() => null),
        ]);
        if (cancelled) return;
        const next = [
          ...alertsFromUsageDoc(snap.exists() ? (snap.data() as Record<string, unknown>) : null),
          ...alertsFromWelcomeBonus(
            welcomeBonusView(grantSnap && grantSnap.exists() ? (grantSnap.data() as Record<string, unknown>) : null)
          ),
        ];
        setItems(next);
        if (next.length === 0) {
          setModalOpen(false);
          return;
        }
        const key = creditAlertDismissKey(workspaceId, periodId, alertSignature(next));
        setModalOpen(!readDismissed(key));
      } catch (error) {
        console.warn('[CreditUsageAlert] failed to load credit usage', error);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [enabled, workspaceId]);

  const signature = useMemo(() => alertSignature(items), [items]);

  if (!enabled || items.length === 0) return null;

  const bannerTone = items.some((item) => item.kind === 'exhausted')
    ? '#b91c1c'
    : items.some((item) => item.kind === 'grace')
      ? '#c2410c'
      : '#b45309';

  return (
    <>
      <div
        role="status"
        style={{
          marginBottom: 12,
          padding: '10px 14px',
          borderRadius: 8,
          background: '#fff7ed',
          border: `1px solid ${bannerTone}`,
          color: bannerTone,
          fontSize: 14,
          lineHeight: 1.5,
        }}
      >
        {items.map((item) => item.message).join(' ')}
      </div>
      {modalOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="credit-alert-title"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            zIndex: 1200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
        >
          <div
            style={{
              width: 'min(480px, 100%)',
              background: '#fff',
              borderRadius: 12,
              padding: 24,
              boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
            }}
          >
            <h2 id="credit-alert-title" style={{ margin: '0 0 12px', fontSize: 18 }}>
              {uiText("AI usage quota notice")}
            </h2>
            <ul style={{ margin: '0 0 20px', paddingLeft: 18, color: '#334155', lineHeight: 1.6 }}>
              {items.map((item) => (
                <li key={`${item.kind}-${item.message}`}>{item.message}</li>
              ))}
            </ul>
            <button
              type="button"
              onClick={() => {
                writeDismissed(creditAlertDismissKey(workspaceId, period, signature));
                setModalOpen(false);
              }}
              style={{
                background: 'var(--workspace-primary, #cfae91)',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '8px 16px',
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              {uiText("Close")}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
